//will emit: "where am I" (based on zero-width strokes, upper left coordinates)
//will emit: "what am I" (the bliss code with no internal coordinates)
//will display itself.
//consists of one part:
//   1. pointer-events: none, normal shape, overflow: visible, box based on zero-width strokes.

import { BlissSVGBuilder } from "bliss-svg-builder";
import { css, html, LitElement } from "lit";

class MovableShape extends LitElement {
  static get properties() {
    return {
      /* bliss code, i.e. "H;C8:0,8" */
      _code: {
        type: String,
      },
      /* px multiplier, i.e. 9 means a full size character will be 8*9=72px wide and 8*9=72px high */
      _size: {
        type: Number,
      },
      _origWidth: {
        type: Number,
      },
      _origHeight: {
        type: Number,
      },
      _origX: {
        type: Number,
      },
      _origY: {
        type: Number,
      },
      _origInternalX: {
        type: Number,
      },
      _origInternalY: {
        type: Number,
      },
      _svg: {
        type: String,
      },
      data: {
        type: Object,
      },
      _origPointerX: {
        type: Number,
      },
      _origPointerY: {
        type: Number,
      },
      _active: {
        type: Boolean,
      },
    };
  }

  constructor() {
    super();
    this._size = 12;
    this.data = {};
    this._active = false;
  }

  updated(changedProperties) {
    if (changedProperties.has("data")) {
      const data = this.data;
      if (!data || !data.code) return;

      // All stored coords are in viewport space (from getBoundingClientRect / event.clientX)
      this._origWidth = data.width;
      this._origHeight = data.height;
      this._origX = data.x;
      this._origY = data.y;
      this._origInternalX = data.internalX;
      this._origInternalY = data.internalY;
      this._code = data.code;
      this._origPointerX = data.pointerX;
      this._origPointerY = data.pointerY;

      // Convert SVG viewport position to local coords for CSS left/top
      // (position:absolute inside a CSS transform needs local coords)
      // Subtract internalX/Y because the render adds them back as margin-left/margin-top
      const board = this.getRootNode().host;
      const boardRect = board.getBoundingClientRect();
      const scale = boardRect.width / board.offsetWidth || 1;
      this._scale = scale;
      this._localX = (data.x - boardRect.left) / scale - data.internalX;
      this._localY = (data.y - boardRect.top) / scale - data.internalY;

      const bt = new BlissSVGBuilder(`[grid=0;stroke-width=0.5;freestyle=1]||${this._code}`);
      this._svg = bt.svgElement;
      this._svg.id = "svg";

      if (/X/.test(data.code)) {
        const pathEl = this._svg.children[1];
        if (pathEl?.children[0]) {
          pathEl.children[0].style.fill = "var(--bl-color-primary)";
        }
      }

      const [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] = this._svg
        .getAttribute("viewBox")
        .split(" ")
        .map(Number);
      this._svgWidth = viewBoxWidth * this._size;
      this._svgHeight = viewBoxHeight * this._size;
      // Content offset from SVG element edge (viewBoxX is negative, so -viewBoxX is positive)
      this._contentOffsetX = -viewBoxX * this._size;
      this._contentOffsetY = -viewBoxY * this._size;
      this._active = true;
    }
  }

  async firstUpdated() {
    const handlePointerup = (event) => {
      if (this._active) {
        // Shape content position in viewport: SVG edge + content offset + pointer delta
        const shapeX = this._origX + this._contentOffsetX * this._scale + (event.clientX - this._origPointerX);
        const shapeY = this._origY + this._contentOffsetY * this._scale + (event.clientY - this._origPointerY);

        const shapeDroppedEvent = new CustomEvent("shape-dropped", {
          detail: { message: "shape dropped.", x: shapeX, y: shapeY, code: this._code },
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(shapeDroppedEvent);
      }
      this._active = false;
    };

    const handlePointermove = (event) => {
      if (!this._active) return;

      const leftMouseButtonOnlyDown = event.buttons === undefined ? event.which === 1 : event.buttons === 1;

      if (leftMouseButtonOnlyDown) {
        // Viewport delta converted to local (unscaled) space for CSS translate
        const relativeX = (event.clientX - this._origPointerX) / this._scale;
        const relativeY = (event.clientY - this._origPointerY) / this._scale;

        // Shape content position in viewport: SVG edge + content offset + pointer delta
        const shapeX = this._origX + this._contentOffsetX * this._scale + (event.clientX - this._origPointerX);
        const shapeY = this._origY + this._contentOffsetY * this._scale + (event.clientY - this._origPointerY);
        const shapeWidth = this._origWidth;
        const shapeHeight = this._origHeight;

        const svg = this.shadowRoot.getElementById("svg");
        svg.style.transform = `translate(${relativeX}px, ${relativeY}px)`;
        const shapeMovedEvent = new CustomEvent("shape-moved", {
          detail: {
            message: "shape moved.",
            x: shapeX,
            y: shapeY,
            width: shapeWidth,
            height: shapeHeight,
            code: this._code,
            svg: svg,
          },
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(shapeMovedEvent);
        event.preventDefault();
      }
    };

    window.addEventListener("pointermove", handlePointermove);
    window.addEventListener("pointerup", handlePointerup);
  }

  static get styles() {
    return css`
      :host {
        position: absolute;
        overflow: visible;
        z-index: 9999;
      }

      svg {
        position: absolute;
        stroke: var(--bl-color-primary);
      }

      svg > path {
        pointer-events: stroke;
      }
    `;
  }

  render() {
    return html`
      <style>
        :host {
          display: ${this._active ? `inline-block` : `none`};
          width: ${this._origWidth}px;
          height: ${this._origHeight}px;
          left: ${this._localX}px;
          top: ${this._localY}px;
        }
        svg {
          width: ${this._svgWidth}px;
          height: ${this._svgHeight}px;
          left: ${this._origInternalX}px;
          top: ${this._origInternalY}px;
        }
      </style>
      ${this._active ? html`${this._svg}` : ""}
    `;
  }
}

customElements.define("movable-shape", MovableShape);
